import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'utils/queryString';
import round from 'utils/round';
import classNames from 'classnames';

import { getAssetUrl } from 'editor-utils/template';
import { getImagePreview } from '../../lib/imageTransformer';
import { Draggable, Droppable } from '../../context/dragDropContext';

import withPlaceholderStyle from '.';
import PhotoQuality from '../photoQuality';

const hasPhotoCustomPosition = photo => {
  // center position
  if (photo.x !== 0.5 || photo.y !== 0.5) {
    return true;
  }
  // scale to 1
  if (photo.scale !== 1) {
    return true;
  }

  // photo has default position
  return false;
};

const PhotoPlaceholder = ({ style, ...props }) => {
  const [, , frameWidth, frameHeight] = props.placeholder.displayFrame || props.placeholder.frame;

  const ref = useRef();

  const [sizeInPx, setSizeInPx] = useState({ width: 100, height: 100 });
  useEffect(() => {
    if (props.link) {
      if (ref.current) {
        const { width, height } = ref.current.getBoundingClientRect();
        setSizeInPx({ width, height });
      } else {
        setSizeInPx({ width: 100, height: 100 });
      }
    }
  }, [props.link, props.page.id, props.placeholder]);

  useEffect(() => {
    if (props.photoQuality && props.link && props.photo && props.photo.alive) {
      const { width: photoWidth, height: photoHeight, scale, orientation } = props.photo;

      const currentDPI = Math.min(
        photoWidth / (((orientation % 2 === 0 ? frameWidth : frameHeight) / 2.54) * scale),
        photoHeight / (((orientation % 2 === 0 ? frameHeight : frameWidth) / 2.54) * scale),
      );

      props.photoQuality(currentDPI);
    }
  }, [props.link, props.photo, props.template, frameWidth, frameHeight, props.photoQuality]);

  if (props.link) {
    const handleOnPhotoUpload = e => {
      e.preventDefault();

      props.onUpload(props.placeholder);
    };

    if (!props.photo || props.photo.alive === -1) {
      return (
        <Droppable
          key={`${props.page.id}-${props.placeholder.id}`}
          ref={ref}
          pageIndex={props.page.index}
          placeholderId={props.placeholder.id}
          style={style}
        >
          <Draggable canDrag={false} size={sizeInPx}>
            <button
              style={style}
              className="btn editor-placeholder editor-placeholder-photo-upload"
              onClick={handleOnPhotoUpload}
            >
              ADD
              <br />
              PHOTO
            </button>
          </Draggable>
        </Droppable>
      );
    }

    const handleOnPhotoEdit = e => {
      e.preventDefault();

      props.onEdit(props.placeholder, props.photo);
    };

    const { width: photoWidth, height: photoHeight, scale, orientation } = props.photo;

    // SMALL RESOLUTION CHECK
    const { minDPI, bestDPI } = props.template.template;

    const minWidth = ((orientation % 2 === 0 ? frameWidth : frameHeight) / 2.54) * bestDPI * scale;
    const minHeight = ((orientation % 2 === 0 ? frameHeight : frameWidth) / 2.54) * bestDPI * scale;

    const smallResolution = photoWidth < minWidth || photoHeight < minHeight;

    const currentDPI = Math.min(
      photoWidth / (((orientation % 2 === 0 ? frameWidth : frameHeight) / 2.54) * scale),
      photoHeight / (((orientation % 2 === 0 ? frameHeight : frameWidth) / 2.54) * scale),
    );

    return (
      <Droppable
        key={`${props.page.id}-${props.placeholder.id}`}
        ref={ref}
        pageIndex={props.page.index}
        placeholderId={props.placeholder.id}
        photoKey={props.photo.key}
        style={style}
      >
        <Draggable
          canDrag
          size={sizeInPx}
          pageIndex={props.page.index}
          placeholderId={props.placeholder.id}
          photoKey={props.photo.key}
          src={getImagePreview(props.photo.url, { orientation: props.photo.orientation, highQualityImage: props.highQualityImage })}
        >
          <button
            style={style}
            className="editor-placeholder editor-placeholder-photo-link"
            onClick={handleOnPhotoEdit}
          >
            {props.withQualityCheck && (
              <PhotoQuality
                value={currentDPI}
                minDPI={minDPI}
                bestDPI={bestDPI}
                className="editor-placeholder-photo-resolution"
              />
            )}
          </button>
        </Draggable>
      </Droppable>
    );
  }

  // Photo border
  let borderAsBoxShadow;
  if (props.placeholder.border) {
    if (props.placeholder.border.style === 'solid') {
      const { color: borderColor, thickness: borderThickness } = props.placeholder.border;
      const borderWidthInPx = round(borderThickness * props.pageSizeScale, 2);
      borderAsBoxShadow = `inset 0px 0px 0px ${borderWidthInPx}px ${borderColor}`;
    }
  }

  // Radius
  let borderRadius = {};
  if (props.placeholder.radius) {
    const radiusX = props.placeholder.radius / frameWidth;
    const radiusY = props.placeholder.radius / frameHeight;
    const cssRadiusValue = `${round(radiusX * 100, 2)}% ${round(radiusY * 100, 2)}%`;

    borderRadius = {
      borderTopLeftRadius: cssRadiusValue,
      borderTopRightRadius: cssRadiusValue,
      borderBottomRightRadius: cssRadiusValue,
      borderBottomLeftRadius: cssRadiusValue,
    };
  }

  if (props.photo && props.photo.alive !== -1) {
    const { width: photoWidth, height: photoHeight, orientation } = props.photo;

    // TODO remove filestack for small photos?
    const url = props.disableImageTransform
      ? queryString.addParams(props.photo.url, { __: null })
      : getImagePreview(props.photo.url, { orientation, highQualityImage: props.highQualityImage });
    const backgroundImage = `url(${url})`;

    // Photo position (default is center / cover)
    let backgroundSize = `cover`;
    let backgroundPosition = `50% 50%`;
    if (hasPhotoCustomPosition(props.photo) && photoWidth && photoHeight) {
      let x = 0.5;
      let y = 0.5;
      let scale = 1;
      if (!props.preview) {
        x = props.photo.x;
        y = props.photo.y;
        scale = props.photo.scale;
      }

      let left = 0.5;
      let top = 0.5;

      let width = photoWidth;
      let height = photoHeight;

      // IMAGE IS ROTATED
      if (orientation % 2 === 1) {
        width = photoHeight;
        height = photoWidth;
      }

      // SCALE UP TO FRAME_WIDTH
      let ratio = frameWidth / width;
      width *= ratio;
      height *= ratio;

      // SCALE UP TO FRAME_HEIGHT
      if (height < frameHeight) {
        ratio = frameHeight / height;
        width *= ratio;
        height *= ratio;
      }

      // SCALE BY USER
      width = round(width * scale, 2);
      height = round(height * scale, 2);

      if (frameWidth < width) {
        left = width / 2 - frameWidth * x;
        left /= width - frameWidth;
      }

      if (frameHeight < height) {
        top = height / 2 - frameHeight * y;
        top /= height - frameHeight;
      }

      // RESET RATIO
      if (width / height < frameWidth / frameHeight) {
        ratio = 1;
      }

      backgroundSize = `${Math.ceil(Math.max(ratio, 1) * scale * 100)}%`;
      backgroundPosition = `${round(left * 100, 2)}% ${round(top * 100, 2)}%`;
    }

    return (
      <>
        <div
          style={{
            ...style,
            backgroundImage,
            backgroundSize,
            backgroundPosition,
            boxShadow: borderAsBoxShadow,
            ...borderRadius,
          }}
          className="editor-placeholder"
        />
        {props.placeholder.overlay_image && (
          <div
            style={{
              ...style,
              backgroundImage: `url(${getAssetUrl(
                props.template,
                `/assets_client/images/${props.placeholder.overlay_image}`,
              )})`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              ...borderRadius,
            }}
            className="editor-placeholder"
          />
        )}
      </>
    );
  }

  return (
    <div
      style={{
        ...style,
        // TODO when placeholder is empty dont render border
        boxShadow: props.preview || props.placeholder.required ? borderAsBoxShadow : null,
        ...borderRadius,
      }}
      className={classNames({
        'editor-placeholder': true,
        'editor-placeholder-thumb photo': props.preview,
        'editor-placeholder-photo-empty': props.placeholder.required,
        'editor-placeholder-photo-error': props.photo?.alive === -1,
      })}
    />
  );
};

PhotoPlaceholder.propTypes = {
  style: PropTypes.object.isRequired,
  placeholder: PropTypes.object.isRequired,
  page: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  photo: PropTypes.object,
  link: PropTypes.bool,
  preview: PropTypes.bool,
  highQualityImage: PropTypes.bool,
  withQualityCheck: PropTypes.bool,
  pageSizeScale: PropTypes.number,
  disableImageTransform: PropTypes.bool,
  onEdit: PropTypes.func,
  onUpload: PropTypes.func,
  photoQuality: PropTypes.func,
};

PhotoPlaceholder.defaultProps = {
  photo: null,
  link: false,
  preview: false,
  highQualityImage: false,
  withQualityCheck: false,
  pageSizeScale: 1,
  disableImageTransform: false,
};

export default withPlaceholderStyle(PhotoPlaceholder);
